import React, { useEffect, useRef } from 'react';

import { animated, useSpring } from '@react-spring/web';

import Theme from 'styles/Theme.js';

/*
 * LeadQuizSheet
 *
 * The phone presentation of the walkthrough. A centred card with a close button
 * is a desktop modal wearing a costume — on a phone the thing people reach for
 * is the sheet itself, so this one is dragged rather than dismissed.
 *
 * What makes it feel physical rather than merely animated:
 *
 *   Tracking is 1:1 and respects where the sheet was grabbed. Snapping the
 *   sheet's top to the finger on grab breaks the illusion in the first frame.
 *
 *   Dragging past the top rubber-bands rather than stopping. A hard stop reads
 *   as frozen; resistance reads as responsive with nothing more to give.
 *
 *   Release projects momentum forward — where the gesture was *going*, not
 *   where the finger happened to leave the glass — so a flick throws the sheet
 *   even if it barely moved. The spring then starts at the finger's exact
 *   release velocity, which is the seam between dragging and animating that
 *   most sheets get wrong.
 *
 * Content scrolls inside. Dragging only takes over when that scroller is at the
 * top, so a downward swipe reads the list first and the sheet second.
 */

// Apple's drawer values — damping 0.8, response 0.3 — expressed as react-spring
// tension and friction: tension = ω², friction = 2·damping·ω, ω = 2π/response.
const SPRING_SETTLE = { friction: 33, mass: 1, tension: 440 };
const SPRING_DISMISS = { friction: 40, mass: 1, tension: 400 };

// A flick should be enough on its own, so distance is not the only way out.
const DISMISS_FRACTION = 0.4;
const DRAG_THRESHOLD = 10;

// Apple's projection from Designing Fluid Interfaces. Not the textbook
// v²/2a — this is the exponential decay scroll views actually use.
const project = (velocity, decelerationRate = 0.998) => {
    return (velocity / 1000) * decelerationRate / (1 - decelerationRate);
};

// The further past the boundary, the less the sheet follows.
const rubberband = (overshoot, dimension, constant = 0.55) => {
    return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
};

const LeadQuizSheet = ({ children, onClose, open }) => {

    const scroller = useRef(null);
    const sheet = useRef(null);

    const drag = useRef({
        active: false,
        grabOffset: 0,
        pointerId: null,
        samples: [],
        startY: 0
    });

    const reduceMotion = typeof(window) !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    const [{ y }, api] = useSpring(() => ({
        config: SPRING_SETTLE,
        y: window.innerHeight
    }));

    const getHeight = () => sheet.current?.offsetHeight || window.innerHeight;

    useEffect(() => {
        api.start({
            config: SPRING_SETTLE,
            immediate: reduceMotion,
            y: open ? 0 : getHeight()
        });
    }, [api, open, reduceMotion]);

    // Velocity from the last few moves rather than the last one - a single pair
    // of points at the end of a gesture is mostly noise.
    const getVelocity = () => {
        let samples = drag.current.samples;
        if(samples.length < 2) {
            return 0;
        }
        let first = samples[0];
        let last = samples[samples.length - 1];
        let elapsed = last.time - first.time;
        return elapsed > 0 ? (last.y - first.y) / elapsed : 0;
    };

    const onPointerDown = event => {

        // ignore a second finger arriving mid-drag, or the sheet jumps to it
        if(drag.current.active || event.isPrimary === false) {
            return;
        }

        // let the list have the gesture until it has nothing left to scroll
        if(scroller.current && scroller.current.scrollTop > 0) {
            return;
        }

        drag.current = {
            active: false,
            grabOffset: y.get(),
            pointerId: event.pointerId,
            samples: [{ time: performance.now(), y: event.clientY }],
            startY: event.clientY
        };
    };

    const onPointerMove = event => {

        if(drag.current.pointerId !== event.pointerId) {
            return;
        }

        let delta = event.clientY - drag.current.startY;
        drag.current.samples = drag.current.samples
            .concat({ time: performance.now(), y: event.clientY })
            .slice(-5);

        // a little hysteresis before committing, so a tap is never a drag
        if(drag.current.active === false) {
            if(Math.abs(delta) < DRAG_THRESHOLD) {
                return;
            }
            drag.current.active = true;
            event.currentTarget.setPointerCapture(event.pointerId);
        }

        let next = drag.current.grabOffset + delta;
        api.start({
            immediate: true,
            y: next < 0 ? rubberband(next, getHeight()) : next
        });
    };

    const onPointerUp = event => {

        if(drag.current.pointerId !== event.pointerId) {
            return;
        }
        let wasDragging = drag.current.active;
        drag.current = { active: false, grabOffset: 0, pointerId: null, samples: [], startY: 0 };

        if(wasDragging === false) {
            return;
        }

        let height = getHeight();
        let velocity = getVelocity();

        // where the gesture was going, not where the finger stopped
        let projected = y.get() + project(velocity * 1000);

        if(projected > height * DISMISS_FRACTION) {
            api.start({
                config: SPRING_DISMISS,
                onRest: onClose,
                velocity: velocity,
                y: height
            });
            return;
        }

        api.start({ config: SPRING_SETTLE, velocity: velocity, y: 0 });
    };

    return (
        <animated.div
        ref={sheet}
        onPointerCancel={onPointerUp}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
            backgroundColor: Theme.colors.white,
            borderRadius: '24px 24px 0 0',
            bottom: 0,
            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            left: 0,
            // as much of the screen as there is. stopping at 92vh left a
            // visible band of dimmed page above it for no reason
            maxHeight: 'calc(100vh - 12px)',
            position: 'fixed',
            right: 0,
            touchAction: 'pan-y',
            transform: y.to(value => `translate3d(0, ${value}px, 0)`),
            zIndex: 6500
        }}>
            {/* the grabber says "this moves" before anyone has tried it */}
            <div style={{
                display: 'flex',
                flexShrink: 0,
                justifyContent: 'center',
                paddingBottom: 4,
                paddingTop: 10
            }}>
                <div style={{
                    backgroundColor: Theme.colors.lighterGrey,
                    borderRadius: 3,
                    height: 5,
                    width: 38
                }} />
            </div>

            {/* the body that scrolls lives inside the view, because the view is
                what knows which part of itself is chrome. the ref is handed down
                so the drag can still ask whether it is at the top. */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: '14px 22px 22px'
            }}>
                {children(scroller)}
            </div>
        </animated.div>
    )
}

export default LeadQuizSheet;
